import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  HeroCard,
  Card,
  TipBox,
  PrimaryButton,
  SecondaryButton
} from '../shared';
import { formatRelativeTime } from '../../../utils/dashboardState';
import InviteFamilyModal from '../../familytree/InviteFamilyModal';
import { getUser } from '../../../utils/secureStorage';

/**
 * LonelyContentState Component
 *
 * Dashboard state for users with stories but NO connections
 * Goal: URGENTLY get them to invite family (their content has no audience!)
 */
function LonelyContentState({ user, stories }) {
  const navigate = useNavigate();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Get familyId and personId from localStorage
  const getFamilyId = () => {
    const familyId = localStorage.getItem('familyId');
    if (familyId) return familyId;
    const userData = getUser() || {};
    return userData.family_id || userData.familyId || null;
  };

  const getPersonId = () => {
    const userData = getUser() || {};
    return userData.person_id || userData.personId || null;
  };

  const familyId = getFamilyId();
  const personId = getPersonId();

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = (memberWasAdded = false) => {
    setIsInviteModalOpen(false);
    if (memberWasAdded) {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC] p-6">
      <div className="max-w-4xl mx-auto">
        <HeroCard
          emoji="📢"
          title="Your Stories Need an Audience!"
          subtitle={`You have ${stories.length} ${stories.length === 1 ? 'story' : 'stories'}, but no one can see them yet`}
          variant="warning"
        />

        <Card variant="alert">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl">⚠️</span>
            <h2 className="text-2xl font-bold text-red-900">
              Your Stories Are Waiting
            </h2>
          </div>

          <p className="text-lg text-red-800 mb-5 leading-relaxed">
            You've created wonderful content, but your family can't see it until
            they connect with you. Invite them so they can enjoy your stories!
          </p>

          <PrimaryButton
            icon="✉️"
            onClick={handleOpenInviteModal}
            variant="danger"
          >
            Invite Family to See Your Stories
          </PrimaryButton>
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl">📚</span>
            <h2 className="text-2xl font-bold text-gray-800">
              Your Stories (Not Yet Visible)
            </h2>
          </div>

          <div className="space-y-3 mb-5">
            {stories.map(story => (
              <div
                key={story.id}
                className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="text-3xl">{story.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-800">
                        {story.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Created {formatRelativeTime(story.sharedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-bold">
                    No audience yet
                  </div>
                </div>
              </div>
            ))}
          </div>

          <TipBox
            icon="💡"
            title="These stories deserve to be heard!"
            text="As soon as you invite family members, they'll be able to see everything you've created."
          />
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-3xl">👨‍👩‍👧‍👦</span>
            <h2 className="text-2xl font-bold text-gray-800">
              Invite Your Family
            </h2>
          </div>

          <div className="flex items-center justify-center gap-4 my-6">
            {[1, 2, 3].map((index) => (
              <div key={index} className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full border-4 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 text-3xl mb-2">
                  +
                </div>
                <span className="text-sm text-gray-600 font-semibold">
                  Add family
                </span>
              </div>
            ))}
          </div>

          <SecondaryButton
            icon="✉️"
            onClick={handleOpenInviteModal}
            variant="green"
            centered
          >
            Send Invitations
          </SecondaryButton>
        </Card>

        {/* Invite Family Modal */}
        <InviteFamilyModal
          isOpen={isInviteModalOpen}
          onClose={handleCloseInviteModal}
          familyId={familyId}
          rootPersonId={personId}
        />
      </div>
    </div>
  );
}

LonelyContentState.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    avatarInitial: PropTypes.string.isRequired
  }).isRequired,
  stories: PropTypes.array.isRequired
};

export default LonelyContentState;
